/**
 * Phase 2b — /docs/{slug} article reader.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Edit3, Lock, Rss } from "lucide-react";

import DocsLayout from "../../components/docs/DocsLayout";
import MarkdownRenderer from "../../components/docs/MarkdownRenderer";
import RightRailTOC from "../../components/docs/RightRailTOC";
import DecryptedHeading from "../../components/docs/DecryptedHeading";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { useUserAuth } from "../../lib/userAuth";
import { useAuth as useAdminAuth } from "../../lib/auth";
import { styleForSection, SECTION_STYLE_FALLBACK } from "../../lib/sectionStyles";
import DocsNotFound from "./DocsNotFound";

const API = process.env.REACT_APP_BACKEND_URL;


function flattenLeaves(nodes) {
  const out = [];
  (nodes || []).forEach((sec) => {
    (sec.children || []).forEach((leaf) => {
      if (leaf.slug) out.push({ slug: leaf.slug, title: leaf.title, section: sec.title });
    });
  });
  return out;
}


/**
 * Strip a leading H1 from the markdown body — every seed page begins with
 * `# Title` which would otherwise render a duplicate H1 below the layout's
 * decryption-revealed <h1>.
 */
function stripLeadingH1(md) {
  if (!md) return "";
  return md.replace(/^\s*#\s+[^\n]+\n+/, "");
}


function findNeighbors(leaves, currentSlug) {
  const idx = leaves.findIndex((l) => l.slug === currentSlug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? leaves[idx - 1] : null,
    next: idx < leaves.length - 1 ? leaves[idx + 1] : null,
  };
}


export default function DocsArticle() {
  const params = useParams();
  // The router is configured with /docs/:slug+ so nested paths (e.g.
  // internal/team-runbook) resolve via params["*"] OR params.slug being
  // the entire path. Support both.
  const rawSlug = params["*"] || params.slug || "";
  const slug = decodeURIComponent(rawSlug);

  const [page, setPage] = useState(null);
  const [navLeaves, setNavLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auth signals — admin (for "Edit this page") + public account (for internal pages).
  const adminCtx = useAdminAuth();
  const userCtx = useUserAuth();
  const isWikiEditor = !!(
    adminCtx?.user &&
    (adminCtx.user.role === "superadmin" ||
      adminCtx.user.team_role === "owner" ||
      adminCtx.user.team_role === "wiki_editor")
  );

  useDocumentTitle(page ? `${page.title} — QorTrace Docs` : "QorTrace Docs");

  // Inject RSS auto-discovery link tag for the changelog page so feed
  // readers detect /api/docs/changelog.rss without users having to hunt.
  useEffect(() => {
    if (slug !== "changelog") return undefined;
    const link = document.createElement("link");
    link.rel = "alternate";
    link.type = "application/rss+xml";
    link.title = "QorTrace Changelog";
    link.href = `${API}/api/docs/changelog.rss`;
    link.setAttribute("data-testid", "docs-rss-link");
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [slug]);

  // Inject Open Graph meta tags so paste-previews on Slack / LinkedIn / X
  // render a branded card. Only public pages get a custom OG image; internal
  // pages reuse the sitewide default.
  useEffect(() => {
    if (!page || page.visibility === "internal") return undefined;
    const ogImage = `${API}/api/docs/og/${encodeURIComponent(slug)}.png`;
    const tags = [
      { property: "og:type", content: "article" },
      { property: "og:title", content: `${page.title} — QorTrace Docs` },
      { property: "og:description", content: page.summary || "" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${page.title} — QorTrace Docs` },
      { name: "twitter:description", content: page.summary || "" },
      { name: "twitter:image", content: ogImage },
    ];
    const created = tags.map((t) => {
      const m = document.createElement("meta");
      if (t.property) m.setAttribute("property", t.property);
      if (t.name) m.setAttribute("name", t.name);
      m.setAttribute("content", t.content);
      m.setAttribute("data-testid", `docs-og-meta-${(t.property || t.name).replace(/[:.]/g, "-")}`);
      document.head.appendChild(m);
      return m;
    });
    return () => {
      created.forEach((m) => { try { document.head.removeChild(m); } catch { /* ignore */ } });
    };
  }, [page, slug]);

  const heading = page?.title || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setPage(null);
      // Fetch independently — a 404 on the page must not abort the nav fetch
      // since the nav drives the "Did you mean?" suggestions on the 404 surface.
      const navP = axios
        .get(`${API}/api/wiki/nav`, { withCredentials: true })
        .then((r) => { if (!cancelled) setNavLeaves(flattenLeaves(r.data?.nodes || [])); })
        .catch(() => { /* nav optional */ });
      const pageP = axios
        .get(`${API}/api/wiki/pages/${encodeURIComponent(slug)}`, { withCredentials: true })
        .then((r) => { if (!cancelled) setPage(r.data || null); })
        .catch((e) => {
          if (cancelled) return;
          if (e?.response?.status === 404) setError("not_found");
          else setError("load_error");
        });
      await Promise.allSettled([navP, pageP]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const { prev, next } = useMemo(
    () => findNeighbors(navLeaves, slug),
    [navLeaves, slug]
  );

  const breadcrumbSection = useMemo(() => {
    const item = navLeaves.find((l) => l.slug === slug);
    return item?.section;
  }, [navLeaves, slug]);

  // Phase 12.2d — Resolve the section-color accent for this article.
  // Section title comes from navLeaves (which already carries the
  // section field per leaf); when the slug isn't curated into the nav
  // (orphan page) we infer from the slug prefix via the same rules
  // the cmd-K palette uses. The accent is then exposed as a CSS
  // variable on the <article> wrapper so docs.css can colour anchors,
  // <h2>/<h3> borders, inline <code>, and blockquotes without any
  // extra prop drilling.
  const articleAccent = useMemo(() => {
    if (!slug) return SECTION_STYLE_FALLBACK.color;
    if (breadcrumbSection) return styleForSection(breadcrumbSection).color;
    // Fallback inference for orphan slugs not in the curated nav.
    const s = slug.toLowerCase();
    const t = (page?.title || "").toLowerCase();
    const has = (x) =>
      s.startsWith(x) || s.includes("/" + x.replace("/", "")) ||
      t.includes(x.replace("/", "").replace("-", " "));
    let inferred = "";
    if      (has("getting-started") || has("audit-walkthrough") || has("faq"))             inferred = "Get Started";
    else if (has("scanner") || has("scan-lifecycle"))                                      inferred = "Scanner";
    else if (has("audits/") || has("audit-lifecycle"))                                     inferred = "Audits";
    else if (has("atlas/"))                                                                inferred = "Atlas";
    else if (has("methodology") || has("glossary"))                                        inferred = "Methodology";
    else if (has("pqc/") || has("harvest-now") || has("preparing-for-2030"))               inferred = "Post-Quantum Cryptography";
    else if (has("compliance") || has("trust-center") || has("verify-a-certificate"))      inferred = "Compliance";
    else if (has("billing"))                                                               inferred = "Billing & Plans";
    else if (has("team/"))                                                                 inferred = "Team & Access";
    else if (has("integrations"))                                                          inferred = "Integrations";
    else if (has("privacy") || has("data-retention"))                                      inferred = "Privacy & Data";
    else if (has("sdk-") || s.includes("developer"))                                       inferred = "Developers";
    else if (has("enterprise"))                                                            inferred = "Enterprise";
    else if (has("qelli"))                                                                 inferred = "Qelli";
    else if (has("changelog") || has("releases/"))                                         inferred = "Releases";
    return inferred ? styleForSection(inferred).color : SECTION_STYLE_FALLBACK.color;
  }, [slug, breadcrumbSection, page?.title]);

  if (error === "not_found") {
    return <DocsNotFound attemptedSlug={slug} navLeaves={navLeaves} />;
  }

  return (
    <DocsLayout
      currentSlug={slug}
      rightRail={page ? <RightRailTOC markdown={page.body_md} /> : null}
    >
      <article
        className="pt-8 pb-24 qt-docs-article"
        data-testid="docs-article"
        style={{ "--section-accent": articleAccent }}
      >
        {loading && (
          <p
            className="font-mono text-sm"
            style={{ color: "var(--qt-text-3)" }}
            data-testid="docs-article-loading"
          >
            Loading…
          </p>
        )}

        {error === "load_error" && (
          <div
            data-testid="docs-article-error"
            className="px-4 py-3 text-sm font-mono"
            style={{ background: "rgba(255,80,80,0.10)", color: "#ff7575" }}
          >
            Failed to load this page. Try reloading or browse <Link to="/docs" style={{ textDecoration: "underline" }}>all docs</Link>.
          </div>
        )}

        {page && (
          <>
            {/* Breadcrumbs */}
            {breadcrumbSection && (
              <nav
                aria-label="Breadcrumb"
                className="qt-docs-breadcrumb mb-6"
                data-testid="docs-breadcrumb"
              >
                <Link to="/docs">Docs</Link>
                <span aria-hidden="true">/</span>
                <span style={{ color: articleAccent, fontWeight: 600 }}>
                  {breadcrumbSection}
                </span>
              </nav>
            )}

            <DecryptedHeading
              target={heading}
              testId="docs-article-title"
            >
              {page.visibility === "internal" && (
                <span className="qt-docs-internal-badge" data-testid="docs-internal-badge">
                  <Lock size={10} /> Internal
                </span>
              )}
              {slug === "changelog" && (
                <a
                  href={`${API}/api/docs/changelog.rss`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="qt-docs-rss-button"
                  aria-label="Subscribe to the QorTrace changelog RSS feed"
                  title="Subscribe via RSS"
                  data-testid="docs-rss-subscribe-btn"
                >
                  <Rss size={12} /> RSS
                </a>
              )}
            </DecryptedHeading>

            {page.summary && (
              <p
                className="qt-docs-summary"
                data-testid="docs-article-summary"
              >
                {page.summary}
              </p>
            )}

            <MarkdownRenderer>{stripLeadingH1(page.body_md || "")}</MarkdownRenderer>

            {/* Edit this page (wiki_editors only) */}
            {isWikiEditor && (
              <div
                className="qt-docs-edit-footer mt-12 pt-6"
                data-testid="docs-edit-this-page"
              >
                <Link
                  to={`/admin/wiki/edit/${encodeURIComponent(slug)}`}
                  className="qt-docs-edit-link"
                >
                  <Edit3 size={12} />
                  <span>Edit this page</span>
                </Link>
              </div>
            )}

            {/* Prev / Next pager */}
            {(prev || next) && (
              <nav
                className="qt-docs-pager mt-10 pt-6"
                aria-label="Article pager"
                data-testid="docs-pager"
              >
                <div>
                  {prev && (
                    <Link
                      to={`/docs/${prev.slug}`}
                      className="qt-docs-pager-link is-prev"
                      data-testid="docs-pager-prev"
                    >
                      <ChevronLeft size={14} />
                      <div>
                        <div className="qt-docs-eyebrow" style={{ color: "#4B5B76" }}>
                          Previous
                        </div>
                        <div style={{ color: "var(--qt-text-1)" }}>{prev.title}</div>
                      </div>
                    </Link>
                  )}
                </div>
                <div>
                  {next && (
                    <Link
                      to={`/docs/${next.slug}`}
                      className="qt-docs-pager-link is-next"
                      data-testid="docs-pager-next"
                    >
                      <div className="text-right">
                        <div className="qt-docs-eyebrow" style={{ color: "#4B5B76" }}>
                          Next
                        </div>
                        <div style={{ color: "var(--qt-text-1)" }}>{next.title}</div>
                      </div>
                      <ChevronRight size={14} />
                    </Link>
                  )}
                </div>
              </nav>
            )}
          </>
        )}
      </article>
    </DocsLayout>
  );
}
